import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";

const PHASES = [
  { key: "listen", label: "Listen", framework: "Discover" },
  { key: "reveal", label: "Reveal", framework: "Define" },
  { key: "reimagine", label: "Reimagine", framework: "Develop" },
  { key: "guide", label: "Guide", framework: "Deliver" },
];

const MANAGING_ROLES = new Set(["partner", "principal", "managing_principal", "managing principal"]);

// Three partner-voice starter phrases. Sound like a Post-It in the moment —
// not consulting boilerplate. Each opens a sentence; the MP finishes it.
const RATIONALE_TEMPLATES = [
  { id: "just-heard", label: "Just heard something new", text: "Something surfaced in this week's conversation that shifts what we should be paying attention to — " },
  { id: "ready-to-name", label: "Ready to name it", text: "We've listened enough. The pattern is clear now, and it's time to name the real work in a single sentence — " },
  { id: "moving-into-execution", label: "Moving into execution", text: "The strategy has landed and the questions are now about cadence, adoption, and drift — " },
];

/**
 * Managing-Principal-only dialog for changing an engagement's primary emphasis.
 *
 * Governance choices baked in:
 *   • Rationale is REQUIRED and validated client-side (min 12 chars) BEFORE
 *     hitting the API (the API enforces the same rule; this is defense in depth).
 *   • Non-MPs never see the trigger — the parent component decides not to
 *     mount the dialog for them.
 *   • The current emphasis is pre-selected so the MP has to actively choose a
 *     different one — no accidental re-approvals of the same state.
 */
export default function MethodEmphasisDialog({ open, onOpenChange, engagementId, currentEmphasis, onSaved }) {
  const { user } = useAuth();
  // Iter 85.75 · Dialog ALWAYS renders. Backend is the single source of
  // truth for authorization and returns a 403 with a clear message if
  // the caller isn't authorized to change emphasis. This prevents the
  // "button appears but does nothing" symptom when the session's role
  // label is stale relative to the DB.
  const canApprove = true;

  const [phase, setPhase] = useState(currentEmphasis || "listen");
  const [rationale, setRationale] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPhase(currentEmphasis || "listen");
      setRationale("");
    }
  }, [open, currentEmphasis]);

  if (!canApprove) return null;

  const save = async () => {
    if (rationale.trim().length < 12) {
      toast.error("Rationale must be at least 12 characters — every approved change needs a 'why now'.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/engagements/${engagementId}/roadmap/set-emphasis`, {
        phase, rationale: rationale.trim(),
      });
      toast.success(`Primary emphasis set to ${PHASES.find((p) => p.key === phase)?.label}. Recorded in the emphasis trail.`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Emphasis change failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-lg" data-testid="method-emphasis-dialog">
        <DialogHeader className="border-b border-primary/30 pb-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-eyebrow flex items-center gap-1.5">
            <ShieldCheck size={12} weight="fill" /> Managing Principal action
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight" style={{ fontFamily: "Georgia, serif" }}>
            Change primary emphasis
          </DialogTitle>
          <p className="text-xs italic text-muted-foreground mt-1" style={{ fontFamily: "Georgia, serif" }}>
            This appends to the engagement's emphasis trail with your name, the effective date, and your rationale.
          </p>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Primary emphasis</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {PHASES.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPhase(p.key)}
                  data-testid={`emphasis-dialog-phase-${p.key}`}
                  className={`text-left border p-3 transition ${
                    phase === p.key ? "border-primary bg-primary/10" : "border-border/60 hover:border-primary/60"
                  }`}
                >
                  <div className="font-bold" style={{ fontFamily: "Georgia, serif" }}>{p.label}</div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow">{p.framework}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
              Rationale · why this emphasis, why now
            </Label>
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="rationale-templates">
              {RATIONALE_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setRationale(t.text)}
                  data-testid={`rationale-template-${t.id}`}
                  className="text-[0.625rem] font-mono uppercase tracking-widest px-2 py-1 border border-border/60 text-muted-foreground hover:border-primary hover:text-primary transition"
                >
                  {t.label}
                </button>
              ))}
            </div>
            <Textarea
              rows={4}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Two or three sentences the future you can read at a glance and remember what was true here."
              className="rounded-none bg-secondary/40 mt-2 font-serif"
              data-testid="emphasis-dialog-rationale"
            />
            <div className={`mt-1 text-[0.625rem] font-mono uppercase tracking-widest ${
              rationale.trim().length >= 12 ? "text-emerald-500" : "text-amber-500"
            }`}>
              {rationale.trim().length} / 12 minimum characters · templates are starters, finish in your own voice
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-border pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button
            onClick={save}
            disabled={busy || rationale.trim().length < 12 || phase === currentEmphasis}
            className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="emphasis-dialog-save"
          >
            {busy ? "Recording…" : "Approve emphasis change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
